<template>
  <div id="app" class="m-0 p-0">
    <b-navbar toggleable variant="dark" type="dark">
      <b-navbar-brand href="#">
        MS Sample Notice System
      </b-navbar-brand>

      <b-navbar-toggle target="navbar-toggle-collapse">
        <template v-slot:default="{ expanded }">
          <b-icon v-if="expanded" icon="chevron-bar-up"></b-icon>
          <b-icon v-else icon="chevron-bar-down"></b-icon>
        </template>
      </b-navbar-toggle>

      <b-collapse id="navbar-toggle-collapse" is-nav>
        <b-navbar-nav class="ml-auto">
          <b-nav-item v-if="canInstall" @click="installer()">Install App</b-nav-item>
          <b-nav-item v-if="isIOS" v-b-modal.ios-install-modal>Install App</b-nav-item>
          <b-nav-item href="#">About</b-nav-item>
        </b-navbar-nav>
      </b-collapse>
      <b-modal id="ios-install-modal" title="Install on iPhone/iPad" ok-only ok-variant="dark" centered>
        <p>To install this app on your iOS device:</p>
        <ol class="text-left">
          <li>Tap the <strong>Share</strong> button <span style="font-size: 1.2em;">&#x1F4E4;</span> at the bottom of Safari</li>
          <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
          <li>Tap <strong>"Add"</strong> in the top right</li>
        </ol>
        <p class="mt-3 text-muted small">Note: Push notifications are limited on iOS. Check back regularly for new results.</p>
      </b-modal>
    </b-navbar>
    <b-alert
      v-model="showInstallBanner"
      variant="light"
      dismissible
      class="mx-2 mt-2 mb-0 py-2 border"
    >
      <b-button
        v-if="canInstall"
        variant="primary"
        size="sm"
        @click="installer()"
      >Install</b-button>
      <b-button
        v-else-if="isIOS"
        variant="primary"
        size="sm"
        v-b-modal.ios-install-modal
      >Install</b-button>
      <span class="ml-2 small">Add to home screen for quick access</span>
    </b-alert>
    <div class="container-fluid px-2 px-md-4 py-3">
      <!-- PWS Lookup Card -->
      <b-card
        class="mx-auto mb-3 shadow-sm"
        style="max-width: 28rem;"
        body-class="p-3"
      >
        <template #header>
          <div class="d-flex align-items-center">
            <b-icon icon="droplet-fill" class="text-primary mr-2"></b-icon>
            <strong>Public Water System</strong>
          </div>
        </template>
        <b-form @submit.stop.prevent="onSubmit">
          <b-form-group class="mb-2">
            <b-input-group>
              <b-input-group-prepend is-text class="small">
                PWS ID
              </b-input-group-prepend>
              <b-form-input
                id="pws-id"
                v-model="pws_id"
                type="number"
                :formatter="formatPwsId"
                no-wheel
                placeholder="7 digits"
                size="lg"
                class="text-center font-weight-bold"
                @focus="clearPws"
                @keydown.enter="$event.target.blur()"
              ></b-form-input>
              <b-input-group-append v-if="pws_ids.length > 0">
                <b-dropdown
                  variant="outline-secondary"
                  right
                  no-caret
                  title="Recent systems"
                >
                  <template #button-content>
                    <b-icon icon="clock-history"></b-icon>
                  </template>
                  <b-dropdown-item
                    v-for="id in pws_ids"
                    :key="id"
                    @click="pws_id = id"
                  >{{ id }}</b-dropdown-item>
                </b-dropdown>
              </b-input-group-append>
              <b-input-group-append v-if="show_remove_button">
                <b-button
                  variant="outline-secondary"
                  v-b-modal.remove-pws-modal
                  title="Remove from history"
                >
                  <b-icon icon="x"></b-icon>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
          <div class="text-center mb-3" style="min-height: 1.5rem;">
            <span v-if="pws_name" class="text-success font-weight-bold">{{ pws_name }}</span>
            <span v-else-if="pws_id && pws_id.length == 7 && !searching_pws" class="text-danger">PWS Not Found</span>
            <span v-else class="text-muted">Enter a PWS ID above</span>
          </div>
          <b-button
            block
            @click="fetchSamples"
            variant="primary"
            :disabled="!valid_pws_id"
          >
            <b-spinner small v-if="loading_results" class="mr-1"></b-spinner>
            <b-icon v-else icon="arrow-repeat" class="mr-1"></b-icon>
            {{ sample_results ? 'Reload' : 'Load' }} Results
          </b-button>
        </b-form>
        <b-modal
          id="remove-pws-modal"
          title="Remove from History"
          @ok="onRemovePws"
          ok-variant="danger"
          ok-title="Remove"
          centered
        >
          Remove <strong>{{ pws_id }}</strong> from your history?
        </b-modal>
      </b-card>

      <!-- Subscription Expired Warning -->
      <b-alert
        v-model="showSubscriptionExpired"
        variant="warning"
        dismissible
        class="mx-auto mb-3"
        style="max-width: 28rem;"
      >
        <b-icon icon="exclamation-triangle-fill" class="mr-2"></b-icon>
        Your notification subscription has expired. Toggle alerts ON to re-subscribe.
      </b-alert>

      <!-- Notifications Card -->
      <b-card
        v-if="notificationsSupported && valid_pws_id"
        class="mx-auto mb-3 shadow-sm"
        style="max-width: 28rem;"
        body-class="p-3"
      >
        <template #header>
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <b-icon icon="bell-fill" class="text-warning mr-2"></b-icon>
              <strong>Notifications</strong>
            </div>
            <b-button
              v-if="subscriptions.length > 0"
              variant="link"
              size="sm"
              class="p-0 text-muted"
              v-b-modal.settings-modal
              title="Manage subscriptions"
            >
              <b-icon icon="gear"></b-icon>
            </b-button>
          </div>
        </template>
        <div class="d-flex align-items-center justify-content-between">
          <b-form-checkbox
            :disabled="buttonDisabled"
            v-model="notificationsButtonOn"
            @change="toggleSubscription"
            name="check-button"
            switch
          >
            <span class="ml-1">Receive alerts</span>
          </b-form-checkbox>
          <b-button
            v-if="currentPwsSubscribed"
            variant="outline-primary"
            size="sm"
            :disabled="testingNotification"
            @click="testNotification"
          >
            <b-spinner small v-if="testingNotification"></b-spinner>
            <span v-else><b-icon icon="bell" class="mr-1"></b-icon>Test</span>
          </b-button>
        </div>
        <b-alert
          v-model="showTestResult"
          :variant="testResultVariant"
          dismissible
          class="mt-2 mb-0 small"
        >
          {{ testResultMessage }}
        </b-alert>
      </b-card>

      <!-- Settings Modal -->
      <b-modal
        id="settings-modal"
        title="Notification Settings"
        ok-only
        ok-variant="dark"
        centered
      >
        <div v-if="subscriptions.length === 0" class="text-muted text-center py-3">
          No active subscriptions
        </div>
        <div v-else>
          <p class="small text-muted mb-2">Subscribed Systems:</p>
          <b-list-group>
            <b-list-group-item
              v-for="sub in subscriptions"
              :key="sub.pws_id"
              class="d-flex justify-content-between align-items-center py-2"
            >
              <div>
                <strong>{{ sub.pws_id }}</strong>
                <span class="text-muted ml-2 small">{{ sub.pws_name }}</span>
              </div>
              <b-button
                variant="outline-danger"
                size="sm"
                @click="unsubscribePws(sub.pws_id)"
                title="Unsubscribe"
              >
                <b-icon icon="x"></b-icon>
              </b-button>
            </b-list-group-item>
          </b-list-group>
          <p class="small text-muted mt-3 mb-0">
            Toggle "Receive alerts" ON for a PWS to add it here.
          </p>
        </div>
      </b-modal>
    </div>
    <!-- Results Card -->
    <b-card
      v-if="pws_id_v"
      class="mx-auto shadow-sm"
      style="max-width: 28rem;"
      body-class="p-2"
      no-body
    >
      <template #header>
        <div class="d-flex align-items-center justify-content-between py-1">
          <div class="d-flex align-items-center">
            <b-icon icon="clipboard-data" class="text-info mr-2"></b-icon>
            <strong>Results</strong>
            <b-badge v-if="sample_results" variant="secondary" class="ml-2">{{ sample_results.length }}</b-badge>
          </div>
          <b-link v-b-modal.modal-legend class="small">Legend</b-link>
        </div>
      </template>
      <b-alert
        class="m-2 mb-0"
        v-model="show_data_error_alert"
        variant="danger"
        dismissible
      >
        Error fetching results
      </b-alert>
      <b-modal id="modal-legend" ok-variant="dark" ok-only centered title="Legend" size="sm">
        <div class="small">
          <div class="mb-2">
            <strong>Results:</strong>
            <span class="ml-2"><b>A</b> = Absent</span>
            <span class="ml-2"><b>P</b> = Present</span>
          </div>
          <div class="mb-2">
            <strong>Purpose:</strong>
            <span class="ml-2"><b>RT</b> = Routine</span>
            <span class="ml-2"><b>RP</b> = Repeat</span>
            <span class="ml-2"><b>SP</b> = Special</span>
          </div>
          <div>
            <strong>Row Colors:</strong>
            <div class="bg-warning px-2 py-1 mt-1">Rejected</div>
            <div class="bg-danger text-white px-2 py-1 mt-1">Bacteria Present</div>
          </div>
        </div>
      </b-modal>
      <b-table
        v-if="sample_results"
        small
        striped
        hover
        bordered
        responsive
        head-variant="dark"
        :fields="sample_fields"
        :items="sample_results"
        :tbody-tr-class="rowClass"
        class="mb-0 small"
        thead-class="small"
      >
        <template #cell(sample_point)="row">
          <b-link
            href="#"
            @click.prevent="row.toggleDetails"
            class="font-weight-bold"
          >{{ row.item.sample_point }}</b-link>
        </template>
        <template #row-details="row">
          <div class="bg-light p-2 small text-left">
            <table class="w-100">
              <tr>
                <td class="pr-3 text-nowrap"><strong>Sample ID:</strong> {{ row.item.sample_id }}</td>
                <td class="text-nowrap"><strong>Facility:</strong> {{ row.item.st_asgn_ident_cd }}</td>
              </tr>
              <tr>
                <td class="pr-3"><strong>Collector:</strong> {{ row.item.informal_collector }}</td>
                <td v-if="row.item.ff_chlor_res_msr"><strong>Free Cl:</strong> {{ toFixed(row.item.ff_chlor_res_msr, 2) }} ppm</td>
              </tr>
              <tr>
                <td class="pr-3"><strong>Received:</strong> {{ row.item.date_received }}</td>
                <td v-if="row.item.fldtot_chl_res_msr"><strong>Total Cl:</strong> {{ toFixed(row.item.fldtot_chl_res_msr, 2) }} ppm</td>
              </tr>
            </table>
            <div v-if="row.item.reject_reason_cd && row.item.reject_reason_cd.trim()" class="text-danger mt-1">
              <strong>Reject:</strong> {{ row.item.reject_reason_cd }}
            </div>
            <div v-if="row.item.description_text" class="mt-1">
              <strong>Location:</strong> {{ row.item.description_text }}
            </div>
            <div v-if="row.item.comment_text" class="mt-1">
              <strong>Comments:</strong> {{ row.item.comment_text }}
            </div>
          </div>
        </template>
      </b-table>
      <div v-if="!sample_results && !show_data_error_alert" class="text-center text-muted py-3">
        No results found
      </div>
      <template #footer v-if="sample_results">
        <div class="text-center small text-muted">
          Tap sample point for details
        </div>
      </template>
    </b-card>

    <!-- Footer -->
    <footer class="text-center text-muted small py-4 mt-3">
      © 2020-{{ currentYear }} | MS Sample Notice - Version: {{ appVersion }}
    </footer>
  </div>
</template>

<script>
export default {
  name: "App",
  data() {
    return {
      installBtn: "none",
      showInstallBanner: false,
      canInstall: false,
      isIOS: false,
      installer: undefined,
      notificationsSupported: false,
      notificationsEnabled: false,
      notificationsButtonOn: false,
      buttonDisabled: false,
      serviceWorkerRegistration: null,
      show_data_error_alert: false,
      subscription: null,
      push_message: null,
      pws_id: null,
      pws_ids: [],
      pws_id_v: null,
      pws_name: null,
      sample_fields: [
        { key: "sample_point", sortable: true },
        { key: "test_reason", sortable: true, label: "Purpose" },
        { key: "tc_result", sortable: true, label: "Total" },
        { key: "ecoli_result", sortable: true, label: "Ecoli" },
        { key: "dt_collected", sortable: true, label: "DT Collected" }
      ],
      sample_results: null,
      loading_results: false,
      searching_pws: false,
      testingNotification: false,
      showTestResult: false,
      testResultMessage: '',
      testResultVariant: 'success',
      subscriptions: [],
      showSettingsModal: false,
      showSubscriptionExpired: false
    }
  },
  created() {
    console.log("app created")

    // Detect iOS
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream

    // Show install banner for iOS (if not already installed as PWA)
    var isStandalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches
    if (this.isIOS && !isStandalone) {
      this.showInstallBanner = true
    }

    if ("Notification" in window && "serviceWorker" in navigator) {
      this.notificationsSupported = true
    }
    // get public key from server
    this.axios
      .get(`${process.env.VUE_APP_API_PATH}/subscription`)
      .then((res) => {
        // console.log(res)
        localStorage.setItem("applicationServerPublicKey", res.data.public_key)
      })
      .catch((error) => {
        console.error(error)
      })

    // create random ID if we don't have it
    // used to update subscriptions
    if (!localStorage.random_id) {
      localStorage.random_id = this.getRandomString(20)
    }

    let installPrompt

    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault()
      installPrompt = e
      this.canInstall = true
      this.showInstallBanner = true
    })

    this.installer = () => {
      this.installBtn = "none"
      installPrompt.prompt()
      installPrompt.userChoice.then((result) => {
        if (result.outcome === "accepted") {
          // console.log('Install accepted!')
        } else {
          // console.log('Install denied!')
        }
      })
    }
  },
  mounted() {
    console.log("app mounted")
    if (localStorage.pws_id) {
      console.log("loaded pws_id from storage")
      this.pws_id = localStorage.pws_id
      this.pws_id_v = this.pws_id
    }
    if (localStorage.pws_name) {
      console.log("loaded pws_name from storage")
      this.pws_name = localStorage.pws_name
    }
    if (localStorage.pws_ids) {
      this.pws_ids = JSON.parse(localStorage.getItem("pws_ids"))
    }
    // check for existing subscription
    if (navigator.serviceWorker) {
      this.findSubscription().then((sub) => {
        if (sub === null) {
          console.log("no active subscription found on the client", sub)
          this.buttonDisabled = false
          this.notificationsEnabled = false
          this.notificationsButtonOn = false
        } else {
          console.log("Active subscription found", sub)
          this.buttonDisabled = false
          this.subscription = sub
          // Fetch server-side subscriptions to know which PWS are subscribed
          this.fetchSubscriptions()
        }
      })
    }
    // get samples for pws
    if (this.pws_id) {
      this.fetchSamples()
    }
  },
  watch: {
    pws_id: function(newVal) {
      if (newVal.length === 7) {
        this.pws_id_v = ""
        this.fetchPwsName().then(() => {
          if (this.pws_name) {
            console.log("match!!! " + this.pws_name)
            this.pws_id_v = newVal
            this.fetchSamples()
            // Update toggle state for new PWS
            this.notificationsButtonOn = this.currentPwsSubscribed
            this.notificationsEnabled = this.currentPwsSubscribed
          }
        })
      }
    }
  },
  computed: {
    appVersion() {
      return process.env.VUE_APP_VERSION
    },
    currentYear() {
      return new Date().getFullYear()
    },
    currentPwsSubscribed() {
      if (!this.pws_id || this.pws_id.length !== 7) return false
      return this.subscriptions.some(function(s) { return s.pws_id === this.pws_id }.bind(this))
    },
    pws_name_msg: function() {
      if (this.pws_name) {
        return "PWS Name: " + this.pws_name
      } else if (
        this.pws_id &&
        this.pws_id.length == 7 &&
        !this.searching_pws
      ) {
        return "PWS Not Found"
      } else {
        return "---"
      }
    },
    show_remove_button: function() {
      return this.pws_ids.length > 1
    },
    historyOptions: function() {
      return this.pws_ids.map(function(id) {
        return { value: id, text: id }
      })
    },
    valid_pws_id: function() {
      return this.pws_id && (this.pws_id.length == 7)
    }
  },
  methods: {
    toFixed(value, precision) {
      let ret = ''
      if (value) {
        ret = value.toFixed(precision)
      }
      return ret
    },
    getRandomString(length) {
      var randomChars =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
      var result = ""
      for (var i = 0; i < length; i++) {
        result += randomChars.charAt(
          Math.floor(Math.random() * randomChars.length)
        )
      }
      return result
    },
    rowClass(item, type) {
      if (!item || type !== "row") return
      if (item.tc_result === "P") return "table-danger"
      if (item.ecoli_result === "P") return "table-danger"
      if (item.reject_reason_cd.trim()) return "table-warning"
    },
    onRemovePws() {
      this.removePws(this.pws_id)
    },
    onSubmit() {
      console.log("submit")
      return false
    },
    clearPws() {
      this.pws_id = ""
      this.pws_id_v = ""
      this.pws_name = ""
      this.sample_results = null
    },
    formatPwsId(e) {
      return String(e).substring(0, 7)
    },
    fetchSamples() {
      // get samples
      this.show_data_error_alert = false
      this.loading_results = true
      this.axios
        .get(`${process.env.VUE_APP_API_PATH}/getsamples`, {
          params: { pws_id: this.pws_id }
        })
        .then((res) => {
          //console.log(res.data.results)
          this.loading_results = false
          if (res.data.results) {
            this.sample_results = res.data.results
            if (this.sample_results.length == 0) {
              this.sample_results = null
            }
          } else {
            this.sample_results = null
          }
        })
        .catch((error) => {
          this.show_data_error_alert = true
          this.loading_results = false
          console.error(error)
        })
    },
    fetchPwsName() {
      // get pws_name
      this.searching_pws = true
      return this.axios
        .get(`${process.env.VUE_APP_API_PATH}/getpws`, {
          params: { pws_id: this.pws_id }
        })
        .then((res) => {
          // console.log(res.data.pws_name)
          this.searching_pws = false
          if (res.data.pws_name) {
            this.pws_name = res.data.pws_name
            this.addPws(this.pws_id, res.data.pws_name)
          } else {
            this.pws_name = null
          }
        })
        .catch((error) => {
          this.searching_pws = false
          console.error(error)
        })
    },
    addPws(pws_id, pws_name) {
      localStorage.pws_id = pws_id
      localStorage.pws_name = pws_name
      if (this.pws_ids.indexOf(pws_id) == -1) {
        this.pws_ids.push(pws_id)
        localStorage.setItem("pws_ids", JSON.stringify(this.pws_ids))
      }
    },
    removePws(pws_id) {
      const index = this.pws_ids.indexOf(pws_id)
      if (index > -1) {
        this.pws_ids.splice(index, 1)
      }
      localStorage.setItem("pws_ids", JSON.stringify(this.pws_ids))
      this.pws_id = ""
      this.pws_name = ""
    },
    urlBase64ToUint8Array(base64String) {
      const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
      const base64 = (base64String + padding)
        .replace(/-/g, "+")
        .replace(/_/g, "/")
      const rawData = window.atob(base64)
      const outputArray = new Uint8Array(rawData.length)
      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i)
      }
      return outputArray
    },
    toggleSubscription() {
      if (this.notificationsSupported) {
        // Toggle subscription for CURRENT PWS only
        if (!this.currentPwsSubscribed) {
          // Subscribe to current PWS
          console.log("Subscribing to PWS: " + this.pws_id)
          Notification.requestPermission().then((result) => {
            if (result === "granted") {
              this.notificationsButtonOn = false
              this.createSubscription()
                .then((sub) => {
                  console.log("subscription created on the client", sub)
                  this.subscription = sub
                  this.notificationsButtonOn = true
                  // store new subscription on the server for this PWS
                  return this.axios.post(
                    `${process.env.VUE_APP_API_PATH}/subscribe`,
                    {
                      subscription: this.subscription,
                      pws_id: this.pws_id,
                      client_id: localStorage.random_id
                    }
                  )
                })
                .then(() => {
                  this.showNotification()
                  this.buttonDisabled = false
                  this.notificationsEnabled = true
                  this.showSubscriptionExpired = false
                  // Refresh subscriptions list
                  this.fetchSubscriptions()
                })
            } else {
              console.log("User did not grant permission")
              this.notificationsButtonOn = false
            }
          })
        } else {
          // Unsubscribe from current PWS only (keep other subscriptions)
          console.log("Unsubscribing from PWS: " + this.pws_id)
          this.unsubscribePws(this.pws_id)
        }
      }
    },
    createSubscription() {
      console.log("ask for active service worker registration")
      if (this.serviceWorkerRegistration === null) {
        return navigator.serviceWorker.ready // returns a Promise, the active SW registration
          .then((swreg) => {
            this.serviceWorkerRegistration = swreg
            return this.subscribe(this.serviceWorkerRegistration)
          })
      } else {
        return this.subscribe(this.serviceWorkerRegistration)
      }
    },
    getSubscription(swreg) {
      console.log("ask for available subscription")
      return swreg.pushManager.getSubscription()
    },
    subscribe(swreg) {
      console.log("create new subscription for this browser on this device")
      // create new subscription for this browser on this device
      // const vapidPublicKey = process.env.VUE_APP_VAPID_PUBLIC_KEY
      const vapidPublicKey = localStorage.getItem("applicationServerPublicKey")
      console.log("swreg: " + swreg)
      console.log(vapidPublicKey)
      const convertedVapidPublicKey = this.urlBase64ToUint8Array(vapidPublicKey)
      console.log(convertedVapidPublicKey)
      // return the subscription promise, we chain another then where we can send it to the server
      return swreg.pushManager.subscribe({
        userVisibleOnly: true,
        // This is for security. On the backend, we need to do something with the VAPID_PRIVATE_KEY
        // that you can find in .env to make this work in the end
        applicationServerKey: convertedVapidPublicKey
      })
    },
    findSubscription() {
      console.log("get active service worker registration")
      return navigator.serviceWorker.ready.then((swreg) => {
        console.log("haal active subscription op")
        this.serviceWorkerRegistration = swreg
        return this.getSubscription(this.serviceWorkerRegistration)
      })
    },
    showNotification() {
      this.serviceWorkerRegistration.showNotification("You are Subscribed", {
        body: "You will receive notifications for new sample results.",
        icon: "/img/icons/android-chrome-192x192.png",
        // image: '/img/autumn-forest.png',
        vibrate: [300, 200, 300]
        // badge: '/img/icons/plint-badge-96x96.png'
      })
    },
    dosubscribe() {
      console.log("subscribe " + this.subscribe)
      console.log(self)
      if (!self.pushmanager) {
        this.push_message = "push notification not supported."
        return
      }
      if (this.subscribe) {
        this.subscribeuser()
      }
    },
    testNotification() {
      this.testingNotification = true
      this.showTestResult = false
      this.axios
        .post(`${process.env.VUE_APP_API_PATH}/test`, {
          client_id: localStorage.random_id,
          pws_id: this.pws_id
        })
        .then((res) => {
          this.testingNotification = false
          if (res.data.success) {
            this.testResultVariant = 'success'
            this.testResultMessage = 'Test notification sent! You should receive it shortly.'
          } else {
            this.testResultVariant = 'danger'
            this.testResultMessage = 'Failed: ' + (res.data.error || 'Unknown error')
          }
          this.showTestResult = true
        })
        .catch((error) => {
          this.testingNotification = false
          this.testResultVariant = 'danger'
          var errMsg = error.message
          if (error.response && error.response.data && error.response.data.error) {
            errMsg = error.response.data.error
          }
          this.testResultMessage = 'Error: ' + errMsg
          this.showTestResult = true
        })
    },
    fetchSubscriptions() {
      if (!localStorage.random_id) return
      this.axios
        .get(`${process.env.VUE_APP_API_PATH}/subscriptions`, {
          params: { client_id: localStorage.random_id }
        })
        .then((res) => {
          this.subscriptions = res.data.subscriptions || []
          // Update toggle state based on current PWS
          this.notificationsButtonOn = this.currentPwsSubscribed
          this.notificationsEnabled = this.currentPwsSubscribed
          // Show warning if browser has subscription but server has none
          if (this.subscription && this.subscriptions.length === 0) {
            this.showSubscriptionExpired = true
          }
        })
        .catch((error) => {
          console.error('Error fetching subscriptions:', error)
        })
    },
    unsubscribePws(pws_id) {
      this.axios
        .post(`${process.env.VUE_APP_API_PATH}/unsubscribe_pws`, {
          client_id: localStorage.random_id,
          pws_id: pws_id
        })
        .then(() => {
          // Remove from local list
          this.subscriptions = this.subscriptions.filter(function(s) { return s.pws_id !== pws_id })
          // Update toggle if we just unsubscribed from current PWS
          if (pws_id === this.pws_id) {
            this.notificationsButtonOn = false
            this.notificationsEnabled = false
          }
        })
        .catch((error) => {
          console.error('Error unsubscribing:', error)
        })
    }
  }
}
</script>

<style>
#app {
  /*font-family: Avenir, Helvetica, Arial, sans-serif;*/
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  /*color: #2c3e50;*/
}
</style>
